import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../jsxElements';
import styles from './TransferLiveAgentFeedback.module.css';
import { logEvent } from '../logging';
import { EllipsisAnimated } from './EllipsisAnimated';
import { LiveAgent } from '../types';
import { format, isUnified } from '../helpers';
import { LiveChatSessionInfoBubble } from './LiveChatSessionInfoBubble';
import {
    OnLiveChatClosedEvent,
    OnLiveChatErrorEvent,
    getLiveChatService,
    LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED,
} from '../liveChatService';
import { AmpFlagSet } from '../amplitude';
import liveChatConfig from '../resources/liveChatConfig.json';
import { LiveChatState } from '../hooks/useLiveChatState';

const maxMinutesWaitTimeDetail = 15;
const liveChatService = getLiveChatService();

class TransferLiveAgentSteps {
    static readonly INITIATED = new TransferLiveAgentSteps(
        liveChatConfig.providence.transferInitiated,
        'live_chat_initialization.svg',
        'live_chat_initialization_error.svg'
    );
    static readonly CONNECTING = new TransferLiveAgentSteps(
        liveChatConfig.providence.waitingForAgent,
        'live_chat_connecting.svg',
        'live_chat_connecting_error.svg'
    );

    static readonly READY = new TransferLiveAgentSteps(
        liveChatConfig.providence.liveAgentReady,
        'live_chat_agent.svg',
        'live_chat_agent_error.svg'
    );

    static get values(): TransferLiveAgentSteps[] {
        return [this.INITIATED, this.CONNECTING, this.READY];
    }

    private constructor(
        public readonly title: string,
        public readonly icon: string,
        public readonly iconError: string | undefined = undefined
    ) {}
}

export const formaWaitTime = (
    waitTime: number | undefined
): string | undefined => {
    if (waitTime === undefined) {
        return undefined;
    }

    const minutes = Math.ceil(waitTime / 60);

    if (minutes < 1) {
        return liveChatConfig.providence.waitTimeLessThanAMinute;
    }

    if (minutes < maxMinutesWaitTimeDetail) {
        return format(liveChatConfig.providence.waitTimeMinutes, {
            waitTime: minutes,
        });
    }

    logEvent({ name: `Very long wait time estimation: ${waitTime}s` });

    return format(liveChatConfig.providence.waitTimeOverMaxMinutes, {
        waitTime: maxMinutesWaitTimeDetail,
    });
};

const formatDescription = (description: any) => {
    return (
        <p className={styles.transferLiveAgentFeedbackWaitTimeLabel}>
            {description}
        </p>
    );
};

const formatError = (isError: string | undefined) => {
    if (isError === LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED) {
        return formatDescription(liveChatConfig.providence.accountError);
    }
    return formatDescription(liveChatConfig.providence.longerThanExpected);
};

const TransferLiveAgentWaitTimeFeedback: React.FC<{
    expectedConnectionInstant: number | undefined; // ms
}> = ({ expectedConnectionInstant }) => {
    const [waitTime, setWaitTime] = useState<string | undefined>(undefined);

    useEffect(() => {
        let waitUpdateInterval: NodeJS.Timeout | undefined;

        if (expectedConnectionInstant === undefined) {
            setWaitTime(undefined);
        } else {
            setWaitTime(
                formaWaitTime((expectedConnectionInstant - Date.now()) / 1000)
            );
            waitUpdateInterval = setInterval(() => {
                setWaitTime(
                    formaWaitTime(
                        (expectedConnectionInstant - Date.now()) / 1000
                    )
                );
            }, 60000);
        }

        return () => {
            if (waitUpdateInterval) {
                clearInterval(waitUpdateInterval);
            }
        };
    }, [expectedConnectionInstant]);

    return formatDescription(
        waitTime ? (
            <span>
                {liveChatConfig.providence.estimatedWait}: {waitTime}
            </span>
        ) : (
            <br />
        )
    );
};

const LiveSessionAgent: React.FC<{
    agent: LiveAgent | undefined;
}> = ({ agent }) => {
    let liveSessionStartMessage: string;
    let liveSessionAgentAvatar: string;

    if (agent?.name === undefined) {
        liveSessionStartMessage =
            liveChatConfig.providence.liveSessionStartNoAgentName;
    } else {
        liveSessionStartMessage = format(
            liveChatConfig.providence.liveSessionStartWithAgentName,
            { name: agent?.name }
        );
    }

    if (agent?.avatar === undefined) {
        liveSessionAgentAvatar = '/live_chat_agent.svg';
    } else {
        liveSessionAgentAvatar = agent?.avatar;
    }

    return (
        <>
            <LiveChatSessionInfoBubble>
                {liveSessionStartMessage}
            </LiveChatSessionInfoBubble>
            <div
                className={
                    styles.transferLiveAgentFeedbackLiveSessionStartedAgentAvatar
                }
            >
                <img src={liveSessionAgentAvatar} />
            </div>
        </>
    );
};

export const TransferLiveAgentFeedback: React.FC<{
    expectedConnectionInstant: number; // ms
    liveChatEnded: boolean;
    currentState: LiveChatState;
    featureFlags: AmpFlagSet;
}> = ({
    expectedConnectionInstant,
    liveChatEnded,
    currentState,
    featureFlags,
}) => {
    const [progress, setProgress] = useState<
        TransferLiveAgentSteps | undefined
    >(TransferLiveAgentSteps.INITIATED);
    const [isError, setError] = useState<string | undefined>(undefined);
    const bottomElRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (liveChatEnded) {
            return;
        }

        const listener = liveChatService.eventsStream.listen((event) => {
            if (event instanceof OnLiveChatClosedEvent) {
                listener.dispose();
            } else if (event instanceof OnLiveChatErrorEvent) {
                setProgress(TransferLiveAgentSteps.CONNECTING);
                setError(event.error);
            }
        });

        return () => {
            listener.dispose();
        };
    }, [liveChatEnded, currentState]);

    useEffect(() => {
        if (
            currentState === LiveChatState.started &&
            progress !== TransferLiveAgentSteps.READY
        ) {
            setProgress(TransferLiveAgentSteps.CONNECTING);
        } else if (currentState === LiveChatState.active) {
            setProgress(TransferLiveAgentSteps.READY);
            bottomElRef.current?.scrollIntoView({ behavior: 'smooth' });
        } else if (currentState === LiveChatState.stopped) {
            setProgress(undefined);
            setError(undefined);
        }
    }, [currentState]);

    const steps = TransferLiveAgentSteps.values;
    const isLastStep = (step: TransferLiveAgentSteps) =>
        step === steps[steps.length - 1];
    const stepIndex = (step: TransferLiveAgentSteps | undefined) => {
        if (step === undefined) {
            return -1;
        }

        return steps.findIndex((value) => step === value);
    };
    const isStepComplete = (step: TransferLiveAgentSteps) =>
        stepIndex(step) < stepIndex(progress);
    const isStepActive = (step: TransferLiveAgentSteps) => step === progress;
    const stepIcon = (step: TransferLiveAgentSteps) => {
        if (isError === undefined || step.iconError === undefined) {
            return '/' + step.icon;
        }

        return '/' + step.iconError;
    };
    const stepIconStyle = (step: TransferLiveAgentSteps) => {
        if (isStepComplete(step)) {
            return styles.transferLiveAgentFeedbackStepIconComplete;
        }

        if (isStepActive(step)) {
            return styles.transferLiveAgentFeedbackStepIconActive;
        }

        return styles.transferLiveAgentFeedbackStepIcon;
    };
    const stepLineStyle = (step: TransferLiveAgentSteps) => {
        if (isStepComplete(step)) {
            return styles.transferLiveAgentFeedbackStepLineComplete;
        }

        if (
            isError !== undefined &&
            step === TransferLiveAgentSteps.CONNECTING
        ) {
            return styles.transferLiveAgentFeedbackStepLineUnknownProgress;
        }

        return styles.transferLiveAgentFeedbackStepLine;
    };
    const footer = () => {
        if (
            progress === undefined ||
            progress === TransferLiveAgentSteps.READY
        ) {
            return <br />;
        }

        if (isError === undefined) {
            return (
                <span>{liveChatConfig.providence.connectAutomatically}</span>
            );
        }

        if (isError === LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED) {
            return <span>{liveChatConfig.providence.logoutAndTryAgain}</span>;
        }

        return <span>{liveChatConfig.providence.refreshBrowser}</span>;
    };
    const stepperStyle = () => {
        if (isError === undefined) {
            return styles.transferLiveAgentFeedbackStepper;
        }

        return styles.transferLiveAgentFeedbackStepperError;
    };

    const cancelButtonData = {
        title: liveChatConfig.providence.cancelChat,
        amplitudeEvent: {
            eventType: 'Billing - Chat with agent - Cancel live chat',
        },
    };
    const cancelButtonHandler = () => {
        liveChatService.userClose();
    };
    const showCancelButton =
        currentState !== LiveChatState.stopped && isUnified();
    const cancelButtonElt = showCancelButton ? (
        <Button
            className={styles.cancelLiveChatButton}
            button={cancelButtonData}
            onClick={cancelButtonHandler}
            disableButtons={false}
            featureFlags={featureFlags}
        />
    ) : null;

    return (
        <div>
            <div className={styles.transferLiveAgentFeedbackContainer}>
                <p className={styles.transferLiveAgentFeedbackTitle}>
                    {progress === undefined
                        ? liveChatConfig.providence.sessionEnded
                        : progress.title}
                </p>
                {isError === undefined && (
                    <TransferLiveAgentWaitTimeFeedback
                        expectedConnectionInstant={
                            progress === TransferLiveAgentSteps.CONNECTING
                                ? expectedConnectionInstant
                                : undefined
                        }
                    />
                )}
                {isError !== undefined && formatError(isError)}
                <div className={stepperStyle()}>
                    {TransferLiveAgentSteps.values.map((step, index) => {
                        return (
                            <React.Fragment key={'step-' + index}>
                                <div
                                    className={
                                        styles.transferLiveAgentFeedbackStepIconWrapper
                                    }
                                >
                                    <img
                                        className={stepIconStyle(step)}
                                        key={'icon-step-' + index}
                                        src={stepIcon(step)}
                                        alt={step.title}
                                    />
                                    {step ===
                                        TransferLiveAgentSteps.CONNECTING &&
                                        progress == step &&
                                        isError === undefined && (
                                            <EllipsisAnimated
                                                className={
                                                    styles.transferLiveAgengFeedbackConnectingEllipsis
                                                }
                                            />
                                        )}
                                </div>
                                {!isLastStep(step) && (
                                    <div
                                        className={stepLineStyle(step)}
                                        key={'line-step-' + index}
                                    >
                                        <div></div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
                {!!cancelButtonElt && (
                    <div className={styles.cancelLiveChatButtonContainer}>
                        {cancelButtonElt}
                    </div>
                )}
                <p className={styles.transferLiveAgentFeedbackInfoLabel}>
                    {footer()}
                </p>
            </div>
            {progress == TransferLiveAgentSteps.READY && (
                <LiveSessionAgent agent={undefined} />
            )}
            <div ref={bottomElRef} />
        </div>
    );
};
